import type { Portfolio } from "@/types/Portfolio";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { WidgetFilterData } from "@/utils/DashboardLayouts";
import { effectiveOrder, replaceKey } from "@/shared/utils/highlightOrder";
import { formatListName } from "@/shared/utils/formatListName";

/**
 * Which record a list from the builder is saved as.
 *
 * The user builds one thing — a watchlist — and never chooses. Typed tickers
 * and keywords alone, matched as "any of these", are a plain watchlist.
 * Anything a watchlist record can't hold makes it a combined watchlist: a tag,
 * another list, a keyword expression, or an AND between the builder's boxes.
 * The typed terms then live in hidden watchlists inside it (component_only),
 * which is how the desk already stores index lists — and pref-storage has no
 * field for tags on a watchlist at all.
 *
 * A combined list stays combined when edited, even if its extras go: it may
 * be selected in dashboards as a combined watchlist, and turning it back would
 * be one more identity change to follow.
 */
export type ListStorage = "watchlist" | "combined";

export interface ListParts {
  hasTypedTerms: boolean;
  /** Tags, picked or brought in by an included combined list. */
  pickedTags: number;
  /** Other watchlists this one combines with. */
  linkedLists: number;
  tagWatchlists: number;
  hasKeywordExpression: boolean;
  /**
   * An AND in force between the boxes. A watchlist matches any of its terms,
   * so "tickers and keywords" needs a combined one.
   */
  usesAnd: boolean;
}

export function storageFor(
  parts: ListParts,
  editing?: ListStorage | "tag"
): ListStorage {
  // A tag watchlist is no longer something to build: saving one makes it a
  // combined watchlist, whatever it holds.
  if (editing === "combined" || editing === "tag") return "combined";
  const extras =
    parts.pickedTags +
    parts.linkedLists +
    parts.tagWatchlists +
    (parts.hasKeywordExpression ? 1 : 0) +
    (parts.usesAnd ? 1 : 0);
  return extras === 0 ? "watchlist" : "combined";
}

/** Portfolio ids a combined list links, from either of its fields. */
const linkedIds = (query: CategoryQuery): number[] =>
  query.portfolioIds ?? (query.portfolios ?? []).map((p) => p.id);

/**
 * The hidden watchlists a combined list keeps its own typed terms in: one, or
 * two once its tickers and keywords are kept apart (typedTermGroups).
 *
 * Only hidden lists with the combined list's own owner: "Build on" copies a
 * Newsquawk combination's linked lists, which can include the desk's own
 * hidden ingredient, and that is the library's to edit, not the user's.
 *
 * And of those, only the list's own. Including another combined watchlist
 * links its hidden lists too (includedLists.ts), and those are the other
 * list's to edit. Its own are named after it — every save renames them with
 * it — or linked from nowhere else; a hidden list named for another list and
 * linked from one is that list's.
 *
 * A Newsquawk list with a single hidden list of the desk's own keeps it as
 * its terms whatever it is called: the desk's index lists were made before
 * names were kept in step, and other desk lists link their constituents, so
 * without this an index opened by the desk would show its constituents as a
 * list chip rather than the tickers to edit.
 */
export function typedTermsWatchlists(
  query: CategoryQuery,
  others: CategoryQuery[] = []
): Portfolio[] {
  const owner = query.owner_type ?? "USER";
  const name = formatListName(query.queryName ?? "");
  const linkedElsewhere = (id: number) =>
    others.some((other) => other.id !== query.id && linkedIds(other).includes(id));
  const hidden = (query.portfolios ?? []).filter(
    (p) => p.component_only && (p.owner_type ?? "USER") === owner
  );
  const own = hidden.filter(
    (p) => formatListName(p.name ?? "") === name || !linkedElsewhere(p.id)
  );
  if (own.length === 0 && owner === "NEWSQUAWK" && hidden.length === 1) return hidden;
  return own;
}

/** One hidden watchlist's worth of typed terms. */
export interface TypedTermGroup {
  tickers: string[];
  keywords: string[];
}

/**
 * The hidden watchlists a combined list's typed terms are saved in: tickers
 * in one, keywords in another.
 *
 * One watchlist holding both is matched two ways. The search files its
 * keywords with the keyword expression and its tickers with the tickers
 * (endpoints.ts), so an AND between the boxes applies; the live matcher ORs
 * such a list with everything (headlineFilterMatcher), so it doesn't. Kept
 * apart, each box matches as it's drawn in both places, and the switch
 * between them is true on the panel's first load and on live headlines.
 */
export function typedTermGroups(tickers: string[], keywords: string[]): TypedTermGroup[] {
  const groups: TypedTermGroup[] = [];
  if (tickers.length > 0) groups.push({ tickers, keywords: [] });
  if (keywords.length > 0) groups.push({ tickers: [], keywords });
  return groups;
}

/**
 * Which existing hidden watchlist each group is saved into, so a list's ids
 * stay put: the one already holding that kind of term, then any spare one.
 * `undefined` means a new one is made. Ids left over are no longer needed.
 */
export function assignTypedSlots(
  existing: Pick<Portfolio, "id" | "tickers" | "keywords">[],
  groups: TypedTermGroup[]
): { slots: (number | undefined)[]; leftover: number[] } {
  const unused = [...existing];
  const take = (holds: (p: (typeof existing)[number]) => boolean) => {
    const index = unused.findIndex(holds);
    return index === -1 ? undefined : unused.splice(index, 1)[0].id;
  };
  const wanted = groups.map((group) =>
    group.tickers.length > 0
      ? (p: (typeof existing)[number]) => (p.tickers?.length ?? 0) > 0
      : (p: (typeof existing)[number]) => (p.keywords?.length ?? 0) > 0
  );
  // Matching kinds first, so a spare list doesn't take a place its kind has.
  const slots: (number | undefined)[] = wanted.map((holds) => take(holds));
  for (let i = 0; i < slots.length; i++) {
    if (slots[i] === undefined) slots[i] = take(() => true);
  }
  return { slots, leftover: unused.map((p) => p.id) };
}

/** Whether any list but this one links the watchlist. */
export function isLinkedElsewhere(
  queries: CategoryQuery[] | undefined,
  portfolioId: number,
  exceptQueryId?: number
): boolean {
  return (queries ?? []).some(
    (q) => q.id !== exceptQueryId && linkedIds(q).includes(portfolioId)
  );
}

/**
 * Whether a combined watchlist includes the tag watchlist.
 *
 * Such a tag watchlist is saved in place rather than converted: converting
 * deletes it, and pref-storage drops the link with it, so the list including
 * it would silently lose its tags.
 */
export function isIncludedTagWatchlist(
  queries: CategoryQuery[] | undefined,
  tagWatchlistId: number
): boolean {
  return (queries ?? []).some((q) => q.tagsWatchlistIds?.includes(tagWatchlistId));
}

/** The dashboard sections each kind of list is selected under. */
const SECTIONS = {
  watchlist: {
    filter: ["ticker-watchlists-filter", "keyword-watchlists-filter"],
    highlight: ["ticker-watchlists-highlight", "keyword-watchlists-highlight"],
  },
  tag: {
    filter: ["tag-watchlists-filter"],
    highlight: ["tag-watchlists-highlight"],
  },
} as const;

/** A list that turns into a combined watchlist when saved in the builder. */
export type ConvertedKind = keyof typeof SECTIONS;

/**
 * Dashboard selections of a list, moved to the combined watchlist it has
 * become.
 *
 * Widgets record a list as a bare id under the section for its kind. A
 * converted watchlist is hidden from the lists those sections are built from,
 * and a converted tag watchlist is deleted; either way, every widget left
 * filtering by it would keep filtering by a row nobody can see or untick, and
 * highlights would silently stop. OR-only combined watchlists filter
 * alongside other lists, so the move is exact.
 *
 * The tag-watchlist sections also hold bare tag ids, and an id there is read
 * as a tag watchlist first (NewsquawkHeadlines), so the id being moved was
 * that tag watchlist wherever it appeared.
 *
 * A watchlist keeps its place in each widget's highlight order: the combined
 * list takes its key there rather than joining the bottom.
 *
 * Returns the same object when nothing referred to the list, so callers can
 * skip the write.
 */
export function moveSelectionsToCombined(
  widgetFilters: Record<string, WidgetFilterData> | undefined,
  kind: ConvertedKind,
  listId: number,
  combinedId: number
): Record<string, WidgetFilterData> | undefined {
  if (!widgetFilters) return widgetFilters;
  const from = String(listId);
  const to = String(combinedId);
  let changed = false;
  const next: Record<string, WidgetFilterData> = {};

  for (const [widgetId, data] of Object.entries(widgetFilters)) {
    let widget = data;
    for (const verb of ["filter", "highlight"] as const) {
      const selections = widget[verb];
      if (!selections) continue;
      const holding = SECTIONS[kind][verb].filter((key) =>
        selections[key]?.includes(from)
      );
      if (holding.length === 0) continue;

      const moved: Record<string, string[]> = { ...selections };
      for (const key of holding) {
        moved[key] = moved[key].filter((id) => id !== from);
      }
      const combinedKey = `category-${verb}`;
      const current = moved[combinedKey] ?? [];
      moved[combinedKey] = current.includes(to) ? current : [...current, to];

      widget = { ...widget, [verb]: moved };
      if (verb === "highlight" && kind === "watchlist") {
        const order = replaceKey(
          effectiveOrder(data.highlight, data.highlightOrder),
          `watchlist:${listId}`,
          `combined:${combinedId}`
        );
        if (order) widget = { ...widget, highlightOrder: order };
      }
      changed = true;
    }
    next[widgetId] = widget;
  }
  return changed ? next : widgetFilters;
}
